// UN/ECE Recommendation 20 - Common Unit Codes
// https://unece.org/trade/uncefact/cl-recommendations

export interface UnitCode {
  code: string;
  nameKey: string; // Translation key for i18n
  symbol?: string;
  category: 'piece' | 'weight' | 'volume' | 'length' | 'area' | 'time' | 'service' | 'other';
}

// Most commonly used unit codes in business
export const UNIT_CODES: UnitCode[] = [
  // Piece/Count Units
  { code: 'H87', nameKey: 'units.piece', symbol: 'Stk', category: 'piece' }, // Most common for invoices
  { code: 'C62', nameKey: 'units.oneUnit', symbol: '1', category: 'piece' }, // One (unit)
  { code: 'EA', nameKey: 'units.each', symbol: 'St', category: 'piece' },
  { code: 'XBX', nameKey: 'units.box', symbol: 'Box', category: 'piece' },
  { code: 'XPK', nameKey: 'units.package', symbol: 'Pkg', category: 'piece' },
  { code: 'XPP', nameKey: 'units.piece', symbol: 'Stück', category: 'piece' }, // Alternative piece
  { code: 'SET', nameKey: 'units.set', symbol: 'Set', category: 'piece' },
  { code: 'PR', nameKey: 'units.pair', symbol: 'Paar', category: 'piece' },
  { code: 'DZN', nameKey: 'units.dozen', symbol: 'Dz', category: 'piece' },
  { code: 'GRO', nameKey: 'units.gross', symbol: 'Gro', category: 'piece' }, // 144 pieces
  { code: 'PF', nameKey: 'units.pallet', symbol: 'Pal', category: 'piece' },
  { code: 'XRO', nameKey: 'units.roll', symbol: 'Rolle', category: 'piece' },
  { code: 'XSA', nameKey: 'units.sack', symbol: 'Sack', category: 'piece' },
  
  // Weight Units
  { code: 'KGM', nameKey: 'units.kilogram', symbol: 'kg', category: 'weight' },
  { code: 'GRM', nameKey: 'units.gram', symbol: 'g', category: 'weight' },
  { code: 'MGM', nameKey: 'units.milligram', symbol: 'mg', category: 'weight' },
  { code: 'TNE', nameKey: 'units.metricTon', symbol: 't', category: 'weight' },
  { code: 'LBR', nameKey: 'units.pound', symbol: 'lb', category: 'weight' },
  
  // Volume Units
  { code: 'LTR', nameKey: 'units.liter', symbol: 'l', category: 'volume' },
  { code: 'MLT', nameKey: 'units.milliliter', symbol: 'ml', category: 'volume' },
  { code: 'MTQ', nameKey: 'units.cubicMeter', symbol: 'm³', category: 'volume' },
  { code: 'CMQ', nameKey: 'units.cubicCentimeter', symbol: 'cm³', category: 'volume' },
  
  // Length Units
  { code: 'MTR', nameKey: 'units.meter', symbol: 'm', category: 'length' },
  { code: 'CMT', nameKey: 'units.centimeter', symbol: 'cm', category: 'length' },
  { code: 'MMT', nameKey: 'units.millimeter', symbol: 'mm', category: 'length' },
  { code: 'KMT', nameKey: 'units.kilometer', symbol: 'km', category: 'length' },
  { code: 'INH', nameKey: 'units.inch', symbol: 'in', category: 'length' },
  { code: 'FOT', nameKey: 'units.foot', symbol: 'ft', category: 'length' },
  { code: 'YRD', nameKey: 'units.yard', symbol: 'yd', category: 'length' },
  
  // Area Units
  { code: 'MTK', nameKey: 'units.squareMeter', symbol: 'm²', category: 'area' },
  { code: 'CMK', nameKey: 'units.squareCentimeter', symbol: 'cm²', category: 'area' },
  { code: 'KMK', nameKey: 'units.squareKilometer', symbol: 'km²', category: 'area' },
  
  // Time Units
  { code: 'HUR', nameKey: 'units.hour', symbol: 'h', category: 'time' },
  { code: 'MIN', nameKey: 'units.minute', symbol: 'min', category: 'time' },
  { code: 'SEC', nameKey: 'units.second', symbol: 's', category: 'time' },
  { code: 'DAY', nameKey: 'units.day', symbol: 'd', category: 'time' },
  { code: 'WEE', nameKey: 'units.week', symbol: 'Wo', category: 'time' },
  { code: 'MON', nameKey: 'units.month', symbol: 'Mo', category: 'time' },
  { code: 'ANN', nameKey: 'units.year', symbol: 'J', category: 'time' },
  { code: 'QAN', nameKey: 'units.quarter', symbol: 'Q', category: 'time' },
  
  // Service Units (very common in business)
  { code: 'P1', nameKey: 'units.percent', symbol: '%', category: 'service' },
  { code: 'LS', nameKey: 'units.lumpSum', symbol: 'pauschal', category: 'service' },
  { code: 'ACT', nameKey: 'units.activity', symbol: 'Tätigkeit', category: 'service' },
  { code: 'NAR', nameKey: 'units.numberOfArticles', symbol: 'Anz', category: 'service' },
  { code: 'E48', nameKey: 'units.serviceUnit', symbol: 'SU', category: 'service' },
  { code: 'XWW', nameKey: 'units.work', symbol: 'Arbeit', category: 'service' },
];

// Helper function to get unit by code
export const getUnitByCode = (code: string): UnitCode | undefined => {
  return UNIT_CODES.find(unit => unit.code === code);
};

// Helper function to get all units grouped by category
export const getUnitsGroupedByCategory = () => {
  const grouped: Record<string, UnitCode[]> = {};
  
  UNIT_CODES.forEach(unit => {
    if (!grouped[unit.category]) {
      grouped[unit.category] = [];
    }
    grouped[unit.category].push(unit);
  });
  
  return grouped;
};

// Helper function to search units
export const searchUnits = (searchTerm: string, t: (key: string) => string): UnitCode[] => {
  const lowerSearch = searchTerm.toLowerCase();
  
  return UNIT_CODES.filter(unit => {
    const translatedName = t(unit.nameKey).toLowerCase();
    const symbol = unit.symbol?.toLowerCase() || '';
    const code = unit.code.toLowerCase();
    
    return translatedName.includes(lowerSearch) || 
           symbol.includes(lowerSearch) || 
           code.includes(lowerSearch);
  });
};
